import React, { useCallback, useState } from "react";
import {
  X,
  Send,
  User,
  MessageSquare,
  Calendar,
  Tag,
  Upload,
  ImageIcon,
  Pencil,
  Check,
} from "lucide-react";
import {
  OdoDetailItem,
  DirectReplyRequest,
  DirectReplyUpdateRequest,
  DirectReplyDeleteRequest,
} from "../../types/odo";
import ConfirmationModal from "./ConfirmationModal";
import { convertFileToBase64, prepareImageForAPI } from "../../utils/imageUtils";
import toast from "react-hot-toast";

interface OdoModalProps {
  isOpen: boolean;
  onClose: () => void;
  odoDetail: OdoDetailItem | null;
  onReplySubmit: (data: DirectReplyRequest) => Promise<boolean>;
  onReplyUpdate: (data: DirectReplyUpdateRequest) => Promise<boolean>;
  onReplyDelete: (data: DirectReplyDeleteRequest) => Promise<boolean>;
  isLoading?: boolean;
}

export default function OdoModal({
  isOpen,
  onClose,
  odoDetail,
  onReplySubmit,
  onReplyUpdate,
  onReplyDelete,
  isLoading = false,
}: OdoModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyImages, setReplyImages] = useState<string[]>([]);

  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingImages, setEditingImages] = useState<string[]>([]);

  // 확인 모달 상태들
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    replyId: "",
  });
  const [updateConfirmModal, setUpdateConfirmModal] = useState({
    isOpen: false,
    replyId: "",
  });

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("ko-KR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // 이미지 문자열을 배열로 변환
  const parseImages = (imageString: string): string[] => {
    if (!imageString) return [];
    return imageString.split(",").filter((url) => url.trim());
  };

  // 이미지 데이터를 출력하는 함수
  const getImageSrc = (imageData: string): string => {
    if (imageData.startsWith("http") || imageData.startsWith("data:")) {
      return imageData;
    }
    return `data:image/jpeg;base64,${imageData}`;
  };

  const getServiceTypeLabel = (serviceType: string) => {
    const types = {
      home: "Home",
      basic_studio: "Basic Studio",
      aistudio: "AI Studio",
      expert_update: "Expert Update",
    };
    return types[serviceType as keyof typeof types] || serviceType;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "답변대기":
        return "bg-yellow-500/20 text-yellow-400";
      case "추가 답변대기":
        return "bg-orange-500/20 text-orange-400";
      case "답변완료":
        return "bg-green-500/20 text-green-400";
      default:
        return "bg-gray-500/20 text-gray-400";
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(async (file) => {
      try {
        const base64Data = await convertFileToBase64(file, {
          includeDataPrefix: false,
          maxSize: 5 * 1024 * 1024
        });
        setReplyImages((prev) => [...prev, base64Data]);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "파일 업로드 중 오류가 발생했습니다.";
        toast.error(`${file.name}: ${errorMessage}`);
      }
    });
  };

  const removeImage = (index: number) => {
    setReplyImages((prev) => prev.filter((_, i) => i !== index));
  };

  // 답변 전송
  const handleSubmitReply = async () => {
    if (!replyText.trim()) {
      console.error("답변 내용이 비어있습니다.");
      return;
    }

    setIsSubmitting(true);
    try {
      const replyData: DirectReplyRequest = {
        inquiry_id: odoDetail!.id,
        description: replyText,
        reference_image: replyImages.join(","),
      };

      const success = await onReplySubmit(replyData);
      if (success) {
        setReplyText("");
        setReplyImages([]);
      }
    } catch (error) {
      console.error("답변 전송 중 오류:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 답변 삭제 확인 모달 
  const handleReplyDelete = useCallback((replyId: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      replyId,
    });
  }, []);

  // 실제 답변 삭제 실행
  const confirmReplyDelete = useCallback(async () => {
    if (!deleteConfirmModal.replyId) {
      console.error("답변 ID가 없습니다.");
      return;
    }

    setIsSubmitting(true);
    try {
      await onReplyDelete({ id: deleteConfirmModal.replyId });
      setDeleteConfirmModal({ isOpen: false, replyId: "" });
    } catch (error) {
      console.error("답변 삭제 중 오류:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [deleteConfirmModal.replyId, onReplyDelete]);

  const closeDeleteConfirmModal = useCallback(() => {
    if (!isSubmitting) {
      setDeleteConfirmModal({ isOpen: false, replyId: "" });
    }
  }, [isSubmitting]);

  // 답변 수정 시작
  const startEditing = (
    chatId: string,
    description: string,
    images: string
  ) => {
    setEditingReplyId(chatId);
    setEditingText(description);
    setEditingImages(parseImages(images));
  };

  // 답변 수정 취소
  const cancelEditing = () => {
    setEditingReplyId(null);
    setEditingText("");
    setEditingImages([]);
  };

  // 답변 수정 이미지 업로드
  const handleEditingImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(async (file) => {
      try {
        const base64Data = await convertFileToBase64(file, {
          includeDataPrefix: false,
          maxSize: 5 * 1024 * 1024
        });
        setEditingImages((prev) => [...prev, base64Data]);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "파일 업로드 중 오류가 발생했습니다.";
        toast.error(`${file.name}: ${errorMessage}`);
      }
    });
  };

  // 답변 수정 이미지 삭제
  const removeEditingImage = (index: number) => {
    setEditingImages((prev) => prev.filter((_, i) => i !== index));
  };

  // 이미지 데이터를 API 전송 형태로 변환하는 함수
  const prepareImageForAPIWrapper = useCallback(
    async (imageData: string): Promise<string> => {
      try {
        return await prepareImageForAPI(imageData);
      } catch (error) {
        console.error("이미지 변환 실패:", imageData, error);
        return "";
      }
    },
    []
  );

  // 답변 수정 확인 모달 
  const handleReplyUpdate = useCallback((replyId: string) => {
    if (!editingText.trim()) {
      console.error("수정할 내용이 비어있습니다.");
      return;
    }

    setUpdateConfirmModal({
      isOpen: true,
      replyId,
    });
  }, [editingText]);

  // 실제 답변 수정 실행
  const confirmReplyUpdate = useCallback(async () => {
    if (!updateConfirmModal.replyId || !editingText.trim()) {
      console.error("수정할 내용이 없습니다.");
      return;
    }

    setIsSubmitting(true);
    try {
      const processedImages = await Promise.all(
        editingImages.map((imageData) => prepareImageForAPIWrapper(imageData))
      );

      const validImages = processedImages.filter((img) => img.trim() !== "");

      const replyData: DirectReplyUpdateRequest = {
        id: updateConfirmModal.replyId,
        description: editingText,
        reference_image: validImages.join(","),
      };

      const success = await onReplyUpdate(replyData);
      if (success) {
        cancelEditing();
        setUpdateConfirmModal({ isOpen: false, replyId: "" });
      }
    } catch (error) {
      console.error("답변 수정 중 오류:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [updateConfirmModal.replyId, editingText, editingImages, onReplyUpdate, prepareImageForAPIWrapper]);

  const closeUpdateConfirmModal = useCallback(() => {
    if (!isSubmitting) {
      setUpdateConfirmModal({ isOpen: false, replyId: "" });
    }
  }, [isSubmitting]);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (
      e.target === e.currentTarget &&
      !isLoading &&
      !isSubmitting &&
      !editingReplyId
    ) {
      onClose();
    }
  };

  if (!isOpen || !odoDetail) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={handleBackgroundClick}
    >
      <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-purple-400" />
            <h3 className="text-xl font-semibold text-white">문의 상세</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading || isSubmitting || editingReplyId !== null}
            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col h-[calc(90vh-140px)]">
          {/* 문의 정보 및 채팅 내용 */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
            {/* 문의 기본 정보 */}
            <div className="bg-slate-800 rounded-lg p-4 space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-white font-medium">
                    {odoDetail.nickname}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-300 text-sm">
                    {formatDate(odoDetail.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-slate-400" />
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                    {getServiceTypeLabel(odoDetail.service_type)}
                  </span>
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    odoDetail.process_status || ""
                  )}`}
                >
                  {odoDetail.process_status}
                </span>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-white mb-2">
                  {odoDetail.title}
                </h4>
                <p className="text-slate-300 whitespace-pre-wrap">
                  {odoDetail.description}
                </p>
              </div>

              {/* 첨부 이미지 */}
              {odoDetail.reference_image && (
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-slate-400">
                    첨부 이미지
                  </h5>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {parseImages(odoDetail.reference_image).map(
                      (imageUrl, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={imageUrl}
                            alt={`첨부 이미지 ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-slate-700"
                            onClick={() => window.open(imageUrl, "_blank")}
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center cursor-pointer">
                            <ImageIcon className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 채팅 목록 */}
            {odoDetail.chats && odoDetail.chats.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-white border-b border-slate-700 pb-2">
                  추가 댓글 ({odoDetail.chats.length}개)
                </h4>
                {odoDetail.chats.map((chat) => (
                  <div
                    key={chat.id}
                    className="bg-slate-800 rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="text-white font-medium">
                          {chat.nickname}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            chat.grade === "admin"
                              ? "bg-purple-500/20 text-purple-400"
                              : "bg-gray-500/20 text-gray-400"
                          }`}
                        >
                          {chat.grade === "admin" ? "관리자" : "사용자"}
                        </span>
                      </div>

                      <div className="flex items-center gap-4">
                        <span className="text-slate-400 text-sm">
                          {formatDate(chat.created_at)}
                        </span>
                        {chat.grade === "admin" && (
                          <div className="flex items-center gap-2">
                            {editingReplyId === chat.id ? (
                              <>
                                <button
                                  onClick={() => handleReplyUpdate(chat.id)}
                                  disabled={isSubmitting || !editingText.trim()}
                                  className="p-1.5 bg-green-700 text-slate-400 hover:text-green-400 hover:bg-green-800 rounded-full transition-colors disabled:opacity-50"
                                >
                                  {isSubmitting ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                                  ) : (
                                    <Check className="w-3 h-3" />
                                  )}
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  disabled={isSubmitting}
                                  className="p-1 bg-slate-700 text-slate-400 hover:text-red-400 hover:bg-red-800 rounded-full transition-colors disabled:opacity-50"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() =>
                                    startEditing(
                                      chat.id,
                                      chat.description,
                                      chat.reference_image || ""
                                    )
                                  }
                                  disabled={isSubmitting}
                                  className="p-1.5 bg-slate-700 text-slate-400 hover:text-blue-400 hover:bg-blue-800 rounded-full transition-colors disabled:opacity-50"
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleReplyDelete(chat.id)}
                                  disabled={isSubmitting}
                                  className="p-1 bg-slate-700 text-slate-400 hover:text-red-400 hover:bg-red-800 rounded-full transition-colors disabled:opacity-50"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {editingReplyId === chat.id ? (
                      // 편집 모드
                      <div className="space-y-3">
                        <textarea
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          placeholder="수정할 내용을 입력하세요..."
                          disabled={isSubmitting}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none disabled:opacity-50"
                          rows={4}
                        />

                        {/* 편집 중인 이미지들 (기존 + 새로 추가) */}
                        {editingImages.length > 0 && (
                          <div className="space-y-2">
                            <h6 className="text-xs font-medium text-slate-400">
                              이미지 목록
                            </h6>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                              {editingImages.map((imageData, index) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={getImageSrc(imageData)}
                                    alt={`이미지 ${index + 1}`}
                                    className="w-full h-20 object-cover rounded-lg border border-slate-600"
                                  />
                                  <button
                                    onClick={() => removeEditingImage(index)}
                                    className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <label className="flex items-center justify-center px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors cursor-pointer text-sm">
                            <Upload className="w-4 h-4 mr-2" />
                            이미지 추가
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleEditingImageUpload}
                              className="hidden"
                              disabled={isSubmitting}
                            />
                          </label>
                        </div>
                      </div>
                    ) : (
                      // 일반 모드
                      <>
                        <p className="text-slate-300 whitespace-pre-wrap">
                          {chat.description}
                        </p>

                        {chat.reference_image && (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {parseImages(chat.reference_image).map(
                              (imageUrl, index) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={imageUrl}
                                    alt={`댓글 이미지 ${index + 1}`}
                                    className="w-full h-24 object-cover rounded-lg border border-slate-700"
                                    onClick={() =>
                                      window.open(imageUrl, "_blank")
                                    }
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center cursor-pointer">
                                    <ImageIcon className="w-4 h-4 text-white" />
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 답변 입력  */}
          <div className="border-t border-slate-800 p-6 bg-slate-900">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h5 className="text-sm font-medium text-slate-300">
                  관리자 답변
                </h5>
                {editingReplyId && (
                  <span className="text-xs text-orange-400">
                    {isSubmitting
                      ? "이미지 변환 및 수정 중..."
                      : "수정 모드 중입니다. 수정을 완료하거나 취소하세요."}
                  </span>
                )}
              </div>

              {replyImages.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {replyImages.map((base64, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={`data:image/jpeg;base64,${base64}`}
                        alt={`업로드 이미지 ${index + 1}`}
                        className="w-full h-20 object-cover rounded-lg border border-slate-700"
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3">
                <div className="flex-1">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={
                      editingReplyId
                        ? "다른 답변 수정을 완료한 후 작성할 수 있습니다."
                        : "답변을 입력하세요..."
                    }
                    disabled={isSubmitting || editingReplyId !== null}
                    className="w-full px-4 py-3 custom-scrollbar bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none disabled:opacity-50"
                    rows={3}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    className={`flex items-center justify-center w-12 h-12 bg-slate-700 text-slate-300 rounded-lg transition-colors ${
                      editingReplyId
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-slate-600 cursor-pointer"
                    }`}
                  >
                    <Upload className="w-5 h-5" />
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={isSubmitting || editingReplyId !== null}
                    />
                  </label>
                  <button
                    onClick={handleSubmitReply}
                    disabled={
                      isSubmitting ||
                      !replyText.trim() ||
                      editingReplyId !== null
                    }
                    className="flex items-center justify-center w-12 h-12 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 답변 삭제 확인 모달 */}
          <ConfirmationModal
            isOpen={deleteConfirmModal.isOpen}
            onClose={closeDeleteConfirmModal}
            onConfirm={confirmReplyDelete}
            title="답변 삭제"
            message={`정말로 이 답변을 삭제하시겠습니까?\n삭제된 답변은 복구할 수 없습니다.`}
            confirmText="삭제"
            cancelText="취소"
            type="danger"
            isLoading={isSubmitting}
          />

          {/* 답변 수정 확인 모달 */}
          <ConfirmationModal
            isOpen={updateConfirmModal.isOpen}
            onClose={closeUpdateConfirmModal}
            onConfirm={confirmReplyUpdate}
            title="답변 수정"
            message="답변을 수정하시겠습니까?"
            confirmText="수정"
            cancelText="취소"
            type="warning"
            isLoading={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
}
